import {AttrsSchema, Component, ConfigOptions, jsx} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {FunnelQueryResponse} from 'common/report/data/funnels/api-types';
import {FunnelBookmark, FunnelSegment} from 'common/report/models/funnels/types';
import {FunnelsQuery} from 'common/report/models/funnels/funnels-query';
import {ColorChoice} from 'common/report/util/funnels/colors';
import {BarVizEl} from 'common/charts/visualization/bar-viz';
import {
  getMedianAccessor,
  getMedianFormatter,
  getMedianToolTipText,
  getTTCShiftedEventIndex,
} from 'common/report/models/funnels/time-to-convert/utils';
import {
  createTooltipDataCallback,
  generateMedianBarAndLegendSegments,
  processFunnelForChartWidget,
} from 'common/report/models/funnels/aggregates';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {useStore} from 'common/util/component-helpers';
import {customEventsStore} from 'common/report/stores/custom-events';
import {getProjectId} from 'common/report/util';
import {DEFAULT_SELECTED_SEGMENTS_LIMIT, MAX_SELECTED_SEGMENTS_LIMIT, ReportName} from 'common/report/constants';

import css from './index.styl?inline';

interface TTCAggregateChartAttrs {
  'project-as-of-date': string;
}

interface TTCAggregateChartState {
  formattedData: FunnelSegment[];
  formattedCompareData: FunnelSegment[];
  selectedSegments: ColorChoice[];
  stepIndex: number;
  query: FunnelsQuery;
  dateRangeLabels: string[];
}

export class FunnelsTTCAggregateChart extends Component<
  TTCAggregateChartState,
  TTCAggregateChartAttrs,
  any,
  any,
  ChartsContextRegistry
> {
  private _funnelsResponse: FunnelQueryResponse;
  private _bookmark: FunnelBookmark;

  static get attrsSchema(): AttrsSchema<TTCAggregateChartAttrs> {
    return {
      ...super.attrsSchema,
      'project-as-of-date': `string`,
    };
  }

  connectedCallback() {
    // Have to call processData after creation of the element in the DOM tree (so the context can be grabbed from parent),
    // but before first render
    this.processData();

    // TODO(sam): make funnels bind to the segment controller too, so that
    // url state/funnel query state updates when segments are toggled
    this.segmentFilterController.bindToComponent(this);

    super.connectedCallback();
    useStore(this, customEventsStore);
  }

  disconnectedCallback() {
    this.segmentFilterController.unbindFromComponent(this);
    super.disconnectedCallback();
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  get config(): ConfigOptions<TTCAggregateChartState, any, ChartsContextRegistry> {
    return {
      useShadowDom: true,
      css,
      contexts: [`segmentFilterController` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController({
          defaultNumSelections: DEFAULT_SELECTED_SEGMENTS_LIMIT[ReportName.Funnels],
          selectAllLimit: MAX_SELECTED_SEGMENTS_LIMIT[ReportName.Funnels],
        }),
      },
      template: () => this.renderTemplate(),
    };
  }

  public set funnelsResponse(response: FunnelQueryResponse) {
    this._funnelsResponse = response;

    this.processData();
  }

  public set bookmark(bookmark: FunnelBookmark) {
    this._bookmark = bookmark;

    this.processData();
  }

  private processData() {
    if (!(this._funnelsResponse && this._bookmark && this.isConnected)) {
      return;
    }

    const stepIndex = getTTCShiftedEventIndex(this._bookmark.eventIndex);

    const {legendSegmentItems, stateUpdates, legendSelectedSegments} = processFunnelForChartWidget(
      this._bookmark,
      this._funnelsResponse,
      Object.values(customEventsStore.value),
      getProjectId(),
      this.attr(`project-as-of-date`),
      stepIndex,
      getMedianAccessor(stepIndex),
    );

    // TODO(sam): change these single arrays to nested when supporting unflattened segments
    this.segmentFilterController.loadSections([
      {
        items: legendSegmentItems,
      },
    ]);

    if (legendSelectedSegments.length) {
      this.segmentFilterController.loadPersistedSelections([
        legendSelectedSegments.map((colorChoice) => colorChoice.key),
      ]);
    }

    this.update({
      ...stateUpdates,
    });
  }

  private renderTemplate() {
    const {dateRangeLabels, formattedData, formattedCompareData, stepIndex} = this.state;

    const segmentFilters = this.segmentFilterController;

    const hasComparisonData = !!formattedCompareData;

    const medianAccessor = getMedianAccessor(stepIndex);

    const segments = generateMedianBarAndLegendSegments({
      // todo(sam) this won't work for unflattened segments
      selectedSegments: segmentFilters.getItems(0).filter((item) => segmentFilters.isItemSelected(0, item.id)),
      formattedData,
      formattedCompareData,
      medianAccessor,
      hasComparisonData,
      stepIndex,
      medianFormatter: getMedianFormatter,
    });

    return (
      <div sel='.ttc-aggregate-widget'>
        <BarVizEl
          attrs={{}}
          props={{
            chartData: segments,
            tooltipDataCallback: createTooltipDataCallback(formattedCompareData, getMedianToolTipText, dateRangeLabels),
          }}
        />
      </div>
    );
  }
}

export const FunnelsTtcAggregateWidgetEl = defineAndMakeJsxFactory(
  `mp-funnels-ttc-aggregate-chart-widget`,
  FunnelsTTCAggregateChart,
);
